import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar.js';
import { Header } from './Header.js';
import { AIChatWidget } from '../ai/AIChatWidget.js';
import { DestructiveToolModal } from '../ai/DestructiveToolModal.js';
import { useUIStore } from '../../stores/uiStore.js';
import { useIsMobile } from '../../modules/tmux/hooks/useIsMobile.js';
import { cn } from '../../lib/utils.js';

const mainMarginMap = {
  expanded: 'ml-64',
  collapsed: 'ml-16',
  hidden: 'ml-0',
} as const;

export function MainLayout() {
  const sidebarState = useUIStore((s) => s.sidebarState);
  const location = useLocation();
  const isMobile = useIsMobile();
  const isAIPage = location.pathname.startsWith('/ai');
  // Desktop compositor is full-screen — no padding, no AI chat widget
  const isDesktopPage = location.pathname.startsWith('/desktop');
  // Chat plugin page is full-screen — needs h-full parent, no p-6 padding
  const isChatPage = location.pathname.startsWith('/chat');
  // Settings hub owns its own layout (nav sidebar + internally-scrolling content pane),
  // so it needs the bounded full-height branch (no outer p-6 / overflow) — see SettingsLayout.
  const isSettingsPage = location.pathname.startsWith('/settings');
  // DM shell (gh#2607 PR-3) — same argument as the settings hub: DmShellLayout owns a
  // fixed tab bar over a pane that scrolls inside itself, so the padded/overflowing
  // branch would scroll the TAB BAR off the top. Matched on `/dm` exactly and `/dm/`,
  // never `startsWith('/dm')` — that would also claim the `/dm-*` legacy redirects and
  // any future `/dmX` route.
  const isDmShellPage = location.pathname === '/dm' || location.pathname.startsWith('/dm/');
  // Mobile tmux fullscreen: hide Header + Sidebar entirely (DOM removal, not CSS hide)
  // Desktop /tmux behaviour is intentionally unchanged — only mobile triggers this.
  const isTmuxFullscreen = isMobile && location.pathname.startsWith('/tmux');

  if (isTmuxFullscreen) {
    return (
      <div className="h-[100dvh] min-h-0 flex flex-col bg-gray-950">
        <Outlet />
        <DestructiveToolModal />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar />
      <Header />
      <main
        className={cn(
          'pt-16 transition-all duration-300 ease-in-out flex flex-col h-[calc(100vh-64px)]',
          mainMarginMap[sidebarState],
        )}
      >
        {isAIPage || isDesktopPage || isChatPage || isSettingsPage || isDmShellPage ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <Outlet />
          </div>
        ) : (
          // `min-w-0` (gh#1527): this content div is a flex item of the column `<main>`. A flex item's
          // default `min-width:auto` lets a wide descendant (e.g. the Console entity grid) force this
          // div past the viewport, pushing content off-screen with no page-level horizontal scroll.
          // Capping min-width to 0 lets it shrink to the available track so an inner scroll container
          // (e.g. the grid's own `overflow-x-auto`) owns the horizontal scroll instead.
          <div className="p-6 flex-1 overflow-y-auto min-w-0">
            <Outlet />
          </div>
        )}
      </main>
      {/* AI Chat Widget — hidden on the AI full page, desktop compositor, and chat plugin page */}
      {!isAIPage && !isDesktopPage && !isChatPage && <AIChatWidget />}
      {/* Destructive tool confirmation — global so it appears even if the user
          navigates away while a tool is paused (roadmap #48 PR-B) */}
      <DestructiveToolModal />
    </div>
  );
}
